import type { FormikValues } from 'formik';
import { useState, useMemo, type FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import Auth from '../../../api/auth';
import useOrgPermissions from '../../../components/AppLayout/hooks/useOrgPermissions';
import GitlabWikiHelpButton from '../../../components/GitlabWikiHelpButton';
import Table from '../../../components/Table';
import { assignableRoles, userRolesLookup } from '../../../helpers/lookups';
import useApiQuery from '../../../hooks/useApiQuery';
import useTableApiCall from '../../../hooks/useTableApiCall';
import rbacCheck from '../../../rbac/rbacCheck';
import OrganizationBreadCrumb from '../OrganizationBreadCrumb';

import CreateUserFormModal from './CreateUserFormModal';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg, ErrorType } from '@/types';
import type { User as UserData } from '@/types/user';

const usersGuideSlug = 'user-guide/organizations/users';

const Users: FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const intl = useIntl();
  const navigate = useNavigate();
  const userData = useUserData();
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const { currentOrganization } = useOrgPermissions();

  const [createModalOpened, setCreateModalOpened] = useState<boolean>(false);
  const [rows, setRows] = useState<UserData[]>([]);

  const { makeApiCall: createUserCall } = useApiQuery(Auth.createV2User as unknown as APIHookArg, {
    successMessage: 'userCreation',
  });

  const userRoles = rbacCheck(
    userData.roles,
    '/users:PUT_ROLE_CHECK',
    currentOrganization.enterpriseName
  ) as string[];

  const canCreateUser = rbacCheck(userData.roles, '/users:POST');

  const endpointParams = useMemo(
    () => ({ organizationId, pagination: true, limit: itemsPerPage }),
    [itemsPerPage, organizationId]
  );
  const memoizedDependency = useMemo(() => [String(itemsPerPage)], [itemsPerPage]);
  const { fetchData, setCurrentPage, ...tableData } = useTableApiCall(Auth.fetchV2Users, {
    isPaginated: true,
    setRows: setRows as (rows: unknown[]) => void,
    searchColumns: ['email', 'roles'],
    endpointParams,
    refetchDeps: memoizedDependency,
  });

  const toggleCreateModal = (): void => {
    setCreateModalOpened(prev => !prev);
  };

  const handleCreateUser = async (formValues: FormikValues): Promise<void> => {
    toggleCreateModal();

    const data = await createUserCall({
      ...formValues,
      organizationId,
    });

    if (data) fetchData();
  };

  const fields = [
    { title: 'Email', id: 'email' },
    { title: 'Role', id: 'roles' },
    { title: 'Activated', id: 'activated' },
  ];

  const transformedRows = rows.map(user => ({
    key: user.uuid,
    onRowClick: () => navigate(`/users/${user.uuid}`),
    data: [
      { field: 'email', value: user.email },
      { field: 'roles', value: userRolesLookup[user.role] },
      { field: 'activated', value: user.activated ? 'True' : 'False' },
    ],
  }));

  const handlePageChange = useCallback(
    (newPage: number) => {
      setCurrentPage(newPage);
    },
    [setCurrentPage]
  );

  return (
    <>
      {createModalOpened && userRoles.length > 0 && (
        <CreateUserFormModal
          roles={assignableRoles(userRoles)}
          onSubmit={values => {
            handleCreateUser(values).catch((error: unknown) => {
              console.error('Error creating user:', error);
            });
          }}
          onCancel={() => {
            setCreateModalOpened(false);
          }}
        />
      )}
      <OrganizationBreadCrumb />
      <Table
        header='Users'
        fields={fields}
        rows={transformedRows}
        headerActionSlot={{
          content: (
            <GitlabWikiHelpButton
              slug={usersGuideSlug}
              tooltipMessageId='usersGuide'
              tooltipDefaultMessage='Users guide'
              modalTitleMessageId='usersGuide'
              modalTitleDefaultMessage='Users guide'
              buttonAriaLabel='Open users guide'
              buttonSx={{ mt: 0 }}
            />
          ),
        }}
        pagination
        {...tableData}
        pageSize={itemsPerPage}
        setItemsPerPage={(size: number) => {
          setItemsPerPage(size);
        }}
        error={tableData.error as ErrorType}
        setFilter={(filter: unknown) => {
          tableData.setFilter(filter as string);
        }}
        onPageChange={handlePageChange}
        createAction={{
          disabled: !canCreateUser,
          onClick: toggleCreateModal,
          tooltip: !canCreateUser ? intl.formatMessage({ id: 'userCreationPermissionErr' }) : '',
        }}
        advancedSearch={false}
      />
    </>
  );
};

export default Users;
